import React, { createContext, useContext, useState, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import apiClient from '../services/api.js';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const decodeUserFromToken = (token) => {
    if (!token) return null;
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (!decodedToken?.exp || decodedToken.exp <= currentTime) {
        return null;
      }

      let roles = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
      if (typeof roles === 'string') {
        roles = [roles];
      }

      return {
        userId: decodedToken.UserId,
        username: decodedToken["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        roles: roles || [],
      };
    } catch {
      return null;
    }
  };

  const applyToken = (token) => {
    const user = decodeUserFromToken(token);
    if (user) {
      setCurrentUser(user);
      setIsAuthenticated(true);
      return;
    }
    localStorage.removeItem('token');
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    applyToken(localStorage.getItem('token'));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleMismatch = () => {
      localStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
      if (window.location.pathname !== '/login' &&
          window.location.pathname !== '/reset-password' &&
          window.location.pathname !== '/forgot-password') {
        window.location.href = '/login';
      }
    };

    const onAuthTokenUpdated = (event) => {
      const nextToken = event?.detail?.token ?? localStorage.getItem('token');
      const nextUser = decodeUserFromToken(nextToken);
      const hydratedUser = event?.detail?.user ?? null;
      const currentUserId = currentUser?.userId ?? null;
      const nextUserId = nextUser?.userId ?? null;

      if (currentUserId && nextUserId && String(currentUserId) !== String(nextUserId)) {
        handleMismatch();
        return;
      }

      if (!nextToken) {
        setCurrentUser(null);
        setIsAuthenticated(false);
        return;
      }

      if (nextUser) {
        if (hydratedUser && (hydratedUser.userId ?? hydratedUser.UserId) && String(hydratedUser.userId ?? hydratedUser.UserId) === String(nextUserId)) {
          setCurrentUser({
            userId: hydratedUser.userId ?? hydratedUser.UserId ?? nextUser.userId,
            username: hydratedUser.username ?? hydratedUser.Username ?? nextUser.username,
            roles: hydratedUser.roles ?? hydratedUser.Roles ?? nextUser.roles,
            permissions: hydratedUser.permissions ?? hydratedUser.Permissions ?? currentUser?.permissions,
          });
        } else {
          setCurrentUser({ ...nextUser, permissions: currentUser?.permissions });
        }
        setIsAuthenticated(true);
        return;
      }

      handleMismatch();
    };

    const onStorage = (event) => {
      if (event.key !== 'token') return;
      const nextToken = event.newValue;
      const nextUser = decodeUserFromToken(nextToken);
      const currentUserId = currentUser?.userId ?? null;
      const nextUserId = nextUser?.userId ?? null;

      if (currentUserId && nextUserId && String(currentUserId) !== String(nextUserId)) {
        handleMismatch();
        return;
      }

      if (!nextToken) {
        setCurrentUser(null);
        setIsAuthenticated(false);
        return;
      }

      if (nextUser) {
        setCurrentUser(nextUser);
        setIsAuthenticated(true);
        return;
      }

      handleMismatch();
    };

    window.addEventListener('authTokenUpdated', onAuthTokenUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('authTokenUpdated', onAuthTokenUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [currentUser?.userId, currentUser?.permissions]);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await apiClient.post('/auth/login', { username, password });
      const { token, userId, username: user, roles, permissions } = response.data;
      
      localStorage.setItem('token', token);
      
      setCurrentUser({
        userId,
        username: user,
        roles,
        permissions
      });
      
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      try {
        window.dispatchEvent(new CustomEvent('authTokenUpdated', { detail: { token: null } }));
      } catch {
      }
      setCurrentUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await apiClient.post('/auth/refresh-token');
      const { token, userId, username, roles, permissions } = response.data;
      
      localStorage.setItem('token', token);
      try {
        window.dispatchEvent(new CustomEvent('authTokenUpdated', { detail: { token } }));
      } catch {
      }
      
      setCurrentUser({
        userId,
        username,
        roles,
        permissions
      });
      
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      try {
        window.dispatchEvent(new CustomEvent('authTokenUpdated', { detail: { token: null } }));
      } catch {
      }
      setCurrentUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      const origin = window.location.origin;
      await apiClient.post('/auth/forgot-password', { email, origin });
      return true;
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to process request');
      throw error;
    }
  };

  const resetPassword = async (token, newPassword, confirmPassword) => {
    try {
      setError(null);
      console.log('Sending reset password request with token:', token);
      
      // Ensure the token is properly formatted and not URL encoded again
      const cleanToken = token.trim();
      
      const response = await apiClient.post('/auth/reset-password', { 
        token: cleanToken, 
        newPassword, 
        confirmPassword 
      });
      
      console.log('Reset password response:', response.status);
      
      // If the response includes a new token, store it and update authentication state
      if (response.data && response.data.token) {
        console.log('New token received after password reset, updating authentication state');
        localStorage.setItem('token', response.data.token);
        
        // If user info is also returned, update the current user
        if (response.data.userId && response.data.username) {
          setCurrentUser({
            userId: response.data.userId,
            username: response.data.username,
            roles: response.data.roles || [],
            permissions: response.data.permissions || []
          });
          setIsAuthenticated(true);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Reset password error details:', error);
      setError(error.response?.data?.message || 'Failed to reset password');
      throw error;
    }
  };

  const register = async (firstName, lastName, username, email, password) => {
    try {
      setError(null);
      await apiClient.post('/auth/register', { firstName, lastName, username, email, password });
      return true;
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      throw error;
    }
  };

  /**
   * Check if the current user has a specific permission
   * @param {string|string[]} requiredPermissions - Permission(s) to check
   * @param {boolean} requireAll - If true, user must have all permissions; if false, any one is sufficient
   * @returns {boolean} - Whether user has the required permission(s)
   */
  const hasPermission = (requiredPermissions, requireAll = false) => {
    // If user is not authenticated, they have no permissions
    if (!isAuthenticated || !currentUser) {
      return false;
    }
    
    // Check if user has Administrator role (which has all permissions)
    if (currentUser.roles && Array.isArray(currentUser.roles) && 
        currentUser.roles.includes('Administrator')) {
      return true;
    }
    
    // If no permissions are required, return true
    if (!requiredPermissions || 
        (Array.isArray(requiredPermissions) && requiredPermissions.length === 0)) {
      return true;
    }
    
    // Convert single permission to array for consistent processing
    const permissions = Array.isArray(requiredPermissions) 
      ? requiredPermissions 
      : [requiredPermissions];
    
    // If user has no permissions, they can't have the required ones
    if (!currentUser.permissions || !Array.isArray(currentUser.permissions)) {
      return false;
    }
    
    // Check if user has the required permissions
    return requireAll
      ? permissions.every(permission => currentUser.permissions.includes(permission))
      : permissions.some(permission => currentUser.permissions.includes(permission));
  };

  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshToken,
    forgotPassword,
    resetPassword,
    register,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
